import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import AuthGuard from '@/components/feature/AuthGuard';
import AuthLayout from '@/pages/auth/components/AuthLayout';

const Splash = lazy(() => import('@/pages/auth/splash/page'));
const Onboarding = lazy(() => import('@/pages/auth/onboarding/page'));
const Login = lazy(() => import('@/pages/auth/login/page'));
const Signup = lazy(() => import('@/pages/auth/signup/page'));
const OTP = lazy(() => import('@/pages/auth/otp/page'));
// forgot-password / reset-password / forgot-mpin are parked, not routed: no
// password-reset or MPIN endpoints exist anywhere (design doc §5.1, B11).
// OTP login is the recovery path. The page files stay on disk, unrouted.
const Lockout = lazy(() => import('@/pages/auth/lockout/page'));
const Home = lazy(() => import('@/pages/home/page'));
const Schemes = lazy(() => import('@/pages/schemes/page'));
const Pay = lazy(() => import('@/pages/pay/page'));
const Spin = lazy(() => import('@/pages/spin/page'));
const SpinGold = lazy(() => import('@/pages/spin-gold/page'));
const Account = lazy(() => import('@/pages/account/page'));
const Profile = lazy(() => import('@/pages/account/profile/page'));
const Ledger = lazy(() => import('@/pages/account/ledger/page'));
const Goals = lazy(() => import('@/pages/account/goals/page'));
const Loans = lazy(() => import('@/pages/account/loans/page'));
const Redemptions = lazy(() => import('@/pages/account/redemptions/page'));
const Payments = lazy(() => import('@/pages/account/payments/page'));
const Notifications = lazy(() => import('@/pages/account/notifications/page'));
const Settings = lazy(() => import('@/pages/account/settings/page'));
const Complaints = lazy(() => import('@/pages/account/complaints/page'));
const Help = lazy(() => import('@/pages/account/help/page'));
const NotFound = lazy(() => import('@/pages/NotFound'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to="/home" replace />,
  },
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      { path: 'splash', element: <Splash /> },
      { path: 'onboarding', element: <Onboarding /> },
      { path: 'login', element: <Login /> },
      { path: 'signup', element: <Signup /> },
      { path: 'otp', element: <OTP /> },
      { path: 'lockout', element: <Lockout /> },
    ],
  },
  {
    path: '/home',
    element: <AuthGuard><Home /></AuthGuard>,
  },
  {
    path: '/schemes',
    element: <AuthGuard><Schemes /></AuthGuard>,
  },
  {
    path: '/pay',
    element: <AuthGuard><Pay /></AuthGuard>,
  },
  {
    path: '/spin',
    element: <AuthGuard><Spin /></AuthGuard>,
  },
  {
    path: '/spin-gold',
    element: <AuthGuard><SpinGold /></AuthGuard>,
  },
  {
    path: '/account',
    element: <AuthGuard><Account /></AuthGuard>,
  },
  {
    path: '/account/profile',
    element: <AuthGuard><Profile /></AuthGuard>,
  },
  {
    path: '/account/ledger',
    element: <AuthGuard><Ledger /></AuthGuard>,
  },
  {
    path: '/account/goals',
    element: <AuthGuard><Goals /></AuthGuard>,
  },
  {
    path: '/account/loans',
    element: <AuthGuard><Loans /></AuthGuard>,
  },
  {
    path: '/account/redemptions',
    element: <AuthGuard><Redemptions /></AuthGuard>,
  },
  {
    path: '/account/payments',
    element: <AuthGuard><Payments /></AuthGuard>,
  },
  {
    path: '/account/notifications',
    element: <AuthGuard><Notifications /></AuthGuard>,
  },
  {
    path: '/account/settings',
    element: <AuthGuard><Settings /></AuthGuard>,
  },
  {
    path: '/account/complaints',
    element: <AuthGuard><Complaints /></AuthGuard>,
  },
  {
    path: '/account/help',
    element: <AuthGuard><Help /></AuthGuard>,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;